// src/services/addressDatabase.service.ts
// Service for fetching real address data from OpenStreetMap via Overpass API
// with Google Geocoding for accurate coordinates

export interface AddressData {
  id: string;
  street: string;
  houseNumber: string;
  unit?: string;
  city?: string;
  state?: string;
  postcode?: string;
  lat: number;
  lng: number;
  source: 'osm' | 'google' | 'openaddresses';
  accuracy?: 'ROOFTOP' | 'RANGE_INTERPOLATED' | 'GEOMETRIC_CENTER' | 'APPROXIMATE';
}

export interface AddressQueryResult {
  addresses: AddressData[];
  source: string;
  queryTime: number;
  bounds: {
    south: number;
    west: number;
    north: number;
    east: number;
  };
}

// Multiple Overpass API servers for fallback
const OVERPASS_SERVERS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
];

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

class AddressDatabaseService {
  private currentServerIndex = 0;

  /**
   * Execute a fetch with retry logic and server fallback
   */
  private async fetchWithRetry(
    query: string,
    onProgress?: (message: string) => void
  ): Promise<any> {
    let lastError: Error | null = null;

    // Try each server
    for (let serverIndex = 0; serverIndex < OVERPASS_SERVERS.length; serverIndex++) {
      const serverUrl = OVERPASS_SERVERS[(this.currentServerIndex + serverIndex) % OVERPASS_SERVERS.length];

      // Retry logic for each server
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          if (attempt > 0 || serverIndex > 0) {
            const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
            onProgress?.(`Retrying... (attempt ${attempt + 1}, server ${serverIndex + 1})`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout

          const response = await fetch(serverUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `data=${encodeURIComponent(query)}`,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            // Remember this server worked well
            this.currentServerIndex = (this.currentServerIndex + serverIndex) % OVERPASS_SERVERS.length;
            return await response.json();
          }

          // Handle specific error codes
          if (response.status === 429) {
            // Rate limited - wait longer and try next server
            onProgress?.('Rate limited, trying another server...');
            lastError = new Error('Rate limited by Overpass API');
            break; // Try next server
          }

          if (response.status === 504 || response.status === 503) {
            // Server timeout or unavailable - retry
            lastError = new Error(`Server error: ${response.status}`);
            continue;
          }

          // Other errors - don't retry
          throw new Error(`Overpass API error: ${response.status}`);
        } catch (error: any) {
          if (error.name === 'AbortError') {
            lastError = new Error('Request timed out. Try drawing a smaller area.');
            onProgress?.('Request timed out, trying another server...');
            break; // Try next server
          }
          lastError = error;
          console.warn(`Overpass fetch attempt ${attempt + 1} failed:`, error.message);
        }
      }
    }

    throw lastError || new Error('All Overpass API servers failed');
  }

  /**
   * Get all addresses within a bounding box using OpenStreetMap Overpass API
   * This returns ACTUAL addresses from the OSM database, not reverse geocoding
   */
  async getAddressesInBounds(
    bounds: { south: number; west: number; north: number; east: number },
    onProgress?: (message: string) => void
  ): Promise<AddressQueryResult> {
    const startTime = Date.now();
    onProgress?.('Querying address database...');

    // Check if area is too large (rough estimate)
    const latDiff = bounds.north - bounds.south;
    const lngDiff = bounds.east - bounds.west;
    const areaSqDeg = latDiff * lngDiff;

    if (areaSqDeg > 0.01) {
      // Approximately 100 sq km depending on location
      onProgress?.('Large area detected, this may take longer...');
    }

    // Overpass QL query to get all addresses in the bounding box
    // This queries both nodes (points) and ways (buildings) with address data
    // Increased timeout to 120 seconds for larger areas
    const query = `
      [out:json][timeout:120];
      (
        node["addr:housenumber"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
        way["addr:housenumber"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
        relation["addr:housenumber"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
      );
      out center;
    `;

    try {
      const data = await this.fetchWithRetry(query, onProgress);
      onProgress?.(`Processing ${data.elements?.length || 0} addresses...`);

      const addresses = this.parseOverpassResponse(data);

      return {
        addresses,
        source: 'OpenStreetMap',
        queryTime: Date.now() - startTime,
        bounds,
      };
    } catch (error: any) {
      console.error('Overpass API error:', error);

      // Provide user-friendly error messages
      if (error.message.includes('timed out')) {
        throw new Error('The area is too large. Please draw a smaller polygon and try again.');
      }
      if (error.message.includes('Rate limited')) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }

      throw new Error(`Failed to fetch addresses: ${error.message}`);
    }
  }

  /**
   * Get addresses within a polygon
   */
  async getAddressesInPolygon(
    polygon: Array<{ lat: number; lng: number }>,
    onProgress?: (message: string) => void
  ): Promise<AddressQueryResult> {
    // First get bounding box
    const bounds = this.getPolygonBounds(polygon);

    // Query addresses in bounding box
    const result = await this.getAddressesInBounds(bounds, onProgress);

    // Filter to only addresses inside the polygon
    onProgress?.('Filtering to selected area...');

    const filteredAddresses = result.addresses.filter(addr =>
      this.pointInPolygon({ lat: addr.lat, lng: addr.lng }, polygon)
    );

    return {
      ...result,
      addresses: filteredAddresses,
    };
  }

  /**
   * Get addresses within a radius of a center point
   */
  async getAddressesInRadius(
    center: { lat: number; lng: number },
    radiusMeters: number,
    onProgress?: (message: string) => void
  ): Promise<AddressQueryResult> {
    const startTime = Date.now();
    onProgress?.('Querying address database...');

    // Overpass query using around filter for circular area
    const query = `
      [out:json][timeout:120];
      (
        node["addr:housenumber"](around:${radiusMeters},${center.lat},${center.lng});
        way["addr:housenumber"](around:${radiusMeters},${center.lat},${center.lng});
      );
      out center;
    `;

    try {
      const data = await this.fetchWithRetry(query, onProgress);
      onProgress?.(`Processing ${data.elements?.length || 0} addresses...`);

      const addresses = this.parseOverpassResponse(data);

      // Calculate actual bounds from the radius
      const bounds = this.radiusToBounds(center, radiusMeters);

      return {
        addresses,
        source: 'OpenStreetMap',
        queryTime: Date.now() - startTime,
        bounds,
      };
    } catch (error: any) {
      console.error('Overpass API error:', error);
      throw new Error(`Failed to fetch addresses: ${error.message}`);
    }
  }

  /**
   * Parse Overpass API response into our address format
   */
  private parseOverpassResponse(data: any): AddressData[] {
    const addresses: AddressData[] = [];
    const seen = new Set<string>();

    for (const element of data.elements || []) {
      const tags = element.tags || {};

      // Must have a house number
      if (!tags['addr:housenumber']) continue;

      // Get coordinates (for ways, use center; for nodes, use lat/lon)
      let lat: number, lng: number;
      if (element.type === 'node') {
        lat = element.lat;
        lng = element.lon;
      } else if (element.center) {
        lat = element.center.lat;
        lng = element.center.lon;
      } else {
        continue; // Skip if no coordinates
      }

      const street = tags['addr:street'] || '';
      const houseNumber = tags['addr:housenumber'];
      const unit = tags['addr:unit'] || tags['addr:flats'] || undefined;

      // Create unique key to dedupe
      const key = `${houseNumber}-${street}-${lat.toFixed(5)}-${lng.toFixed(5)}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      addresses.push({
        id: `osm_${element.type}_${element.id}`,
        street,
        houseNumber,
        unit,
        city: tags['addr:city'] || tags['addr:suburb'] || '',
        state: tags['addr:state'] || '',
        postcode: tags['addr:postcode'] || '',
        lat,
        lng,
        source: 'osm',
      });
    }

    // Sort by street then house number
    addresses.sort((a, b) => {
      const streetCompare = a.street.localeCompare(b.street);
      if (streetCompare !== 0) return streetCompare;

      // Try to sort house numbers numerically
      const numA = parseInt(a.houseNumber, 10);
      const numB = parseInt(b.houseNumber, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return a.houseNumber.localeCompare(b.houseNumber);
    });

    return addresses;
  }

  /**
   * Get bounding box from polygon
   */
  private getPolygonBounds(polygon: Array<{ lat: number; lng: number }>) {
    let south = Infinity, west = Infinity, north = -Infinity, east = -Infinity;

    for (const point of polygon) {
      south = Math.min(south, point.lat);
      west = Math.min(west, point.lng);
      north = Math.max(north, point.lat);
      east = Math.max(east, point.lng);
    }

    return { south, west, north, east };
  }

  /**
   * Convert center + radius to bounding box
   */
  private radiusToBounds(center: { lat: number; lng: number }, radiusMeters: number) {
    // Rough conversion: 1 degree latitude ≈ 111km
    const latDelta = radiusMeters / 111320;
    const lngDelta = radiusMeters / (111320 * Math.cos(center.lat * Math.PI / 180));

    return {
      south: center.lat - latDelta,
      north: center.lat + latDelta,
      west: center.lng - lngDelta,
      east: center.lng + lngDelta,
    };
  }

  /**
   * Check if a point is inside a polygon (ray casting algorithm)
   */
  private pointInPolygon(
    point: { lat: number; lng: number },
    polygon: Array<{ lat: number; lng: number }>
  ): boolean {
    let inside = false;
    const { lat: x, lng: y } = point;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat, yi = polygon[i].lng;
      const xj = polygon[j].lat, yj = polygon[j].lng;

      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }

  /**
   * Refine OSM addresses with Google Geocoding for accurate rooftop coordinates
   * This significantly improves pin placement accuracy
   */
  async refineWithGoogleGeocoding(
    addresses: AddressData[],
    onProgress?: (message: string) => void
  ): Promise<AddressData[]> {
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps not loaded, returning OSM coordinates');
      return addresses;
    }

    const geocoder = new google.maps.Geocoder();
    const refined: AddressData[] = [];
    const batchSize = 10; // Process in batches to avoid rate limits
    const delayBetweenBatches = 200; // ms

    for (let i = 0; i < addresses.length; i += batchSize) {
      const batch = addresses.slice(i, i + batchSize);
      onProgress?.(`Refining coordinates: ${i + batch.length} of ${addresses.length} addresses...`);

      const results = await Promise.all(
        batch.map(async (addr) => {
          try {
            const addressString = this.formatAddress(addr);

            return new Promise<AddressData>((resolve) => {
              geocoder.geocode({ address: addressString }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                  const location = results[0].geometry.location;
                  const accuracy = results[0].geometry.location_type as AddressData['accuracy'];

                  resolve({
                    ...addr,
                    lat: location.lat(),
                    lng: location.lng(),
                    source: 'google',
                    accuracy,
                  });
                } else {
                  // Keep OSM coordinates if Google fails
                  resolve(addr);
                }
              });
            });
          } catch (error) {
            // Keep OSM coordinates on error
            return addr;
          }
        })
      );

      refined.push(...results);

      // Delay between batches to respect rate limits
      if (i + batchSize < addresses.length) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }

    return refined;
  }

  /**
   * Get addresses with refined Google coordinates (slower but more accurate)
   * Falls back to building footprints + Google reverse geocoding if OSM address data is sparse
   */
  async getAddressesInPolygonRefined(
    polygon: Array<{ lat: number; lng: number }>,
    onProgress?: (message: string) => void
  ): Promise<AddressQueryResult> {
    // First try OSM tagged addresses
    const osmResult = await this.getAddressesInPolygon(polygon, onProgress);

    // Only trust OSM if it has substantial data (50+ addresses)
    // In many US areas, OSM has building footprints but very sparse address tags,
    // so a handful of results means we should fall back to building footprints
    if (osmResult.addresses.length >= 50) {
      onProgress?.('Refining address coordinates...');
      const refinedAddresses = await this.refineWithGoogleGeocoding(
        osmResult.addresses,
        onProgress
      );

      return {
        ...osmResult,
        addresses: refinedAddresses,
        source: 'OpenStreetMap + Google Geocoding',
      };
    }

    // OSM has insufficient data - use building footprints + Google reverse geocoding
    if (osmResult.addresses.length > 0) {
      onProgress?.(`Only found ${osmResult.addresses.length} tagged addresses, searching building footprints...`);
    } else {
      onProgress?.('No OSM address data, searching building footprints...');
    }

    try {
      const googleAddresses = await this.discoverAddressesWithGoogle(polygon, onProgress);

      // Merge with any OSM addresses (they may have data buildings miss)
      const merged = this.mergeAddresses(osmResult.addresses, googleAddresses);

      return {
        addresses: merged,
        source: 'OpenStreetMap + Google Geocoding',
        queryTime: osmResult.queryTime,
        bounds: osmResult.bounds,
      };
    } catch (error) {
      console.error('Google fallback failed:', error);
      return osmResult; // Return whatever OSM had
    }
  }

  /**
   * Every building footprint inside the polygon, returned as a workable stop.
   * Buildings that sit on a tagged OSM address inherit it; the rest are kept as
   * pin-only stops (coordinates only) so coverage is truly "every house" — the
   * canvasser fills in details (and units) in the field. No slow per-building
   * reverse-geocoding, so it's fast and doesn't drop un-geocodable buildings.
   */
  async getEveryBuildingInPolygon(
    polygon: Array<{ lat: number; lng: number }>,
    onProgress?: (message: string) => void
  ): Promise<Array<{ houseNumber: string; street: string; city: string; state: string; zipCode: string; lat: number; lng: number; addressed: boolean }>> {
    const bounds = this.getPolygonBounds(polygon);
    onProgress?.('Loading buildings…');
    const buildings = (await this.getBuildingFootprints(bounds, onProgress))
      .filter(b => this.pointInPolygon({ lat: b.lat, lng: b.lng }, polygon));

    onProgress?.('Loading tagged addresses…');
    const tagged = (await this.getAddressesInPolygon(polygon, onProgress)).addresses;

    const meters = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
      const dLat = (a.lat - b.lat) * 111320;
      const dLng = (a.lng - b.lng) * 111320 * Math.cos((a.lat * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng);
    };

    const used = new Set<number>();
    const stops: Array<{ houseNumber: string; street: string; city: string; state: string; zipCode: string; lat: number; lng: number; addressed: boolean }> = [];

    for (const b of buildings) {
      let bestIdx = -1, bestD = 25; // match a tagged address within 25m
      tagged.forEach((t, i) => {
        if (used.has(i)) return;
        const d = meters(b, t);
        if (d < bestD) { bestD = d; bestIdx = i; }
      });
      if (bestIdx >= 0) {
        const t = tagged[bestIdx];
        used.add(bestIdx);
        stops.push({ houseNumber: t.houseNumber, street: t.street, city: t.city || '', state: t.state || '', zipCode: t.postcode || '', lat: b.lat, lng: b.lng, addressed: true });
      } else {
        stops.push({ houseNumber: '', street: '', city: '', state: '', zipCode: '', lat: b.lat, lng: b.lng, addressed: false });
      }
    }

    // Keep tagged addresses that had no building footprint nearby.
    tagged.forEach((t, i) => {
      if (used.has(i)) return;
      stops.push({ houseNumber: t.houseNumber, street: t.street, city: t.city || '', state: t.state || '', zipCode: t.postcode || '', lat: t.lat, lng: t.lng, addressed: true });
    });

    onProgress?.(`${stops.length} stops (${stops.filter(s => s.addressed).length} addressed)`);
    return stops;
  }

  /**
   * Merge two address lists, deduplicating by house number + street
   */
  private mergeAddresses(primary: AddressData[], secondary: AddressData[]): AddressData[] {
    const seen = new Set<string>();
    const merged: AddressData[] = [];

    // Secondary (building footprint/Google) addresses go first since they're usually more numerous,
    // then primary (OSM tagged) addresses fill in any that buildings missed
    for (const addr of [...secondary, ...primary]) {
      const key = `${addr.houseNumber}-${addr.street}`.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(addr);
      }
    }

    merged.sort((a, b) => {
      const streetCompare = a.street.localeCompare(b.street);
      if (streetCompare !== 0) return streetCompare;
      const numA = parseInt(a.houseNumber, 10);
      const numB = parseInt(b.houseNumber, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.houseNumber.localeCompare(b.houseNumber);
    });

    return merged;
  }

  /**
   * Get building footprints from OSM (even without address data)
   * Buildings are often mapped even when addresses aren't
   */
  private async getBuildingFootprints(
    bounds: { south: number; west: number; north: number; east: number },
    onProgress?: (message: string) => void
  ): Promise<Array<{ lat: number; lng: number; type: string }>> {
    onProgress?.('Searching for building footprints...');

    // Query OSM for all buildings (not just ones with addresses)
    const query = `
      [out:json][timeout:120];
      (
        way["building"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
        relation["building"](${bounds.south},${bounds.west},${bounds.north},${bounds.east});
      );
      out center;
    `;

    try {
      const data = await this.fetchWithRetry(query, onProgress);
      const buildings: Array<{ lat: number; lng: number; type: string }> = [];

      for (const element of data.elements || []) {
        // Get center point of the building
        if (element.center) {
          buildings.push({
            lat: element.center.lat,
            lng: element.center.lon,
            type: element.tags?.building || 'yes',
          });
        }
      }

      onProgress?.(`Found ${buildings.length} building footprints`);
      return buildings;
    } catch (error) {
      console.warn('Failed to fetch building footprints:', error);
      return [];
    }
  }

  /**
   * Discover addresses in a polygon using Google reverse geocoding
   * First tries to use OSM building footprints, then falls back to grid
   */
  private async discoverAddressesWithGoogle(
    polygon: Array<{ lat: number; lng: number }>,
    onProgress?: (message: string) => void
  ): Promise<AddressData[]> {
    if (!window.google || !window.google.maps) {
      throw new Error('Google Maps not loaded');
    }

    const bounds = this.getPolygonBounds(polygon);
    const geocoder = new google.maps.Geocoder();
    const addresses: AddressData[] = [];
    const seen = new Set<string>();

    // First, try to get building footprints from OSM
    const buildings = await this.getBuildingFootprints(bounds, onProgress);

    // Filter buildings to only those inside our polygon
    const buildingsInPolygon = buildings.filter(b =>
      this.pointInPolygon({ lat: b.lat, lng: b.lng }, polygon)
    );

    let pointsToGeocode: Array<{ lat: number; lng: number }> = [];

    if (buildingsInPolygon.length > 0) {
      // Use building centers - much smarter than grid!
      onProgress?.(`Found ${buildingsInPolygon.length} buildings to geocode...`);
      pointsToGeocode = buildingsInPolygon;
    } else {
      // Fall back to grid if no building data
      onProgress?.('No building data available, using grid scan...');
      pointsToGeocode = this.generateGridPoints(polygon, bounds);
      onProgress?.(`Generated ${pointsToGeocode.length} grid points to scan...`);
    }

    // Limit total points to avoid excessive API usage
    if (pointsToGeocode.length > 500) {
      onProgress?.(`Limiting to 500 points (from ${pointsToGeocode.length})...`);
      // If we have buildings, prioritize residential types
      if (buildingsInPolygon.length > 0) {
        // Sort to prioritize residential buildings
        const sorted = [...buildingsInPolygon].sort((a, b) => {
          const residentialTypes = ['house', 'residential', 'detached', 'apartments', 'yes'];
          const aScore = residentialTypes.indexOf(a.type) >= 0 ? 0 : 1;
          const bScore = residentialTypes.indexOf(b.type) >= 0 ? 0 : 1;
          return aScore - bScore;
        });
        pointsToGeocode = sorted.slice(0, 500);
      } else {
        pointsToGeocode = pointsToGeocode.slice(0, 500);
      }
    }

    // Process in batches to avoid rate limits
    const batchSize = 10;
    const delayBetweenBatches = 250;
    let processedCount = 0;

    for (let i = 0; i < pointsToGeocode.length; i += batchSize) {
      const batch = pointsToGeocode.slice(i, i + batchSize);

      const batchResults = await Promise.all(
        batch.map(point => this.reverseGeocodePoint(geocoder, point))
      );

      for (const result of batchResults) {
        if (result && !seen.has(result.key)) {
          seen.add(result.key);
          addresses.push(result.address);
        }
      }

      processedCount += batch.length;
      const source = buildingsInPolygon.length > 0 ? 'buildings' : 'grid points';
      onProgress?.(`Discovered ${addresses.length} addresses (${processedCount}/${pointsToGeocode.length} ${source})...`);

      // Delay between batches
      if (i + batchSize < pointsToGeocode.length) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }

    // Sort by street then house number
    addresses.sort((a, b) => {
      const streetCompare = a.street.localeCompare(b.street);
      if (streetCompare !== 0) return streetCompare;
      const numA = parseInt(a.houseNumber, 10);
      const numB = parseInt(b.houseNumber, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.houseNumber.localeCompare(b.houseNumber);
    });

    return addresses;
  }

  /**
   * Generate a grid of points inside a polygon (fallback when no building data)
   */
  private generateGridPoints(
    polygon: Array<{ lat: number; lng: number }>,
    bounds: { south: number; west: number; north: number; east: number }
  ): Array<{ lat: number; lng: number }> {
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;

    // Grid spacing in degrees (aim for ~30 meter spacing)
    const gridSpacingLat = 30 / 111320;
    const gridSpacingLng = 30 / (111320 * Math.cos(((bounds.north + bounds.south) / 2) * Math.PI / 180));

    // Calculate number of points (limit to avoid API overuse)
    const numLatPoints = Math.min(Math.ceil(latSpan / gridSpacingLat), 40);
    const numLngPoints = Math.min(Math.ceil(lngSpan / gridSpacingLng), 40);

    const gridPoints: Array<{ lat: number; lng: number }> = [];

    for (let i = 0; i < numLatPoints; i++) {
      for (let j = 0; j < numLngPoints; j++) {
        const lat = bounds.south + (i + 0.5) * (latSpan / numLatPoints);
        const lng = bounds.west + (j + 0.5) * (lngSpan / numLngPoints);

        if (this.pointInPolygon({ lat, lng }, polygon)) {
          gridPoints.push({ lat, lng });
        }
      }
    }

    return gridPoints;
  }

  /**
   * Reverse geocode a single point and extract address data
   */
  private reverseGeocodePoint(
    geocoder: google.maps.Geocoder,
    point: { lat: number; lng: number }
  ): Promise<{ address: AddressData; key: string } | null> {
    return new Promise((resolve) => {
      geocoder.geocode({ location: point }, (results, status) => {
        if (status !== 'OK' || !results || results.length === 0) {
          resolve(null);
          return;
        }

        // Find the most specific address result (street_address or premise)
        const addressResult = results.find(r =>
          r.types.includes('street_address') ||
          r.types.includes('premise') ||
          r.types.includes('subpremise')
        ) || results.find(r => r.types.includes('route'));

        if (!addressResult) {
          resolve(null);
          return;
        }

        // Extract address components
        const components = addressResult.address_components;
        const streetNumber = components.find(c => c.types.includes('street_number'))?.long_name || '';
        const route = components.find(c => c.types.includes('route'))?.long_name || '';
        const city = components.find(c => c.types.includes('locality'))?.long_name ||
                     components.find(c => c.types.includes('sublocality'))?.long_name || '';
        const state = components.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
        const postcode = components.find(c => c.types.includes('postal_code'))?.long_name || '';
        const unit = components.find(c => c.types.includes('subpremise'))?.long_name;

        // Skip if no street number (not a specific address)
        if (!streetNumber || !route) {
          resolve(null);
          return;
        }

        const location = addressResult.geometry.location;
        const key = `${streetNumber}-${route}-${unit || ''}`.toLowerCase();

        resolve({
          key,
          address: {
            id: `google_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            street: route,
            houseNumber: streetNumber,
            unit,
            city,
            state,
            postcode,
            lat: location.lat(),
            lng: location.lng(),
            source: 'google',
            accuracy: addressResult.geometry.location_type as AddressData['accuracy'],
          },
        });
      });
    });
  }

  /**
   * Format address for display
   */
  formatAddress(addr: AddressData): string {
    const parts = [`${addr.houseNumber} ${addr.street}`.trim()];
    if (addr.unit) parts[0] += ` Unit ${addr.unit}`;
    if (addr.city) parts.push(addr.city);
    if (addr.state && addr.postcode) {
      parts.push(`${addr.state} ${addr.postcode}`);
    } else if (addr.state) {
      parts.push(addr.state);
    } else if (addr.postcode) {
      parts.push(addr.postcode);
    }
    return parts.join(', ');
  }

  /**
   * Calculate distance between two points in meters using Haversine formula
   */
  private haversineDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Get the closest N addresses to a center point
   * Expands radius outward until target count is reached
   */
  async getClosestAddresses(
    center: { lat: number; lng: number },
    targetCount: number,
    onProgress?: (message: string) => void
  ): Promise<AddressQueryResult> {
    const startTime = Date.now();
    const allAddresses: AddressData[] = [];
    const seenCoords = new Set<string>();
    const seenAddresses = new Set<string>();

    // Start with a small radius and expand outward
    let currentRadius = 200; // Start with 200m
    const maxRadius = 5000; // Max 5km
    const radiusIncrement = 200;

    // Cache all buildings across radius expansions to avoid re-querying Overpass
    let allBuildings: Array<{ lat: number; lng: number; type: string }> = [];
    let lastQueriedRadius = 0;

    while (allAddresses.length < targetCount && currentRadius <= maxRadius) {
      onProgress?.(`Searching within ${currentRadius}m (found ${allAddresses.length}/${targetCount})...`);

      // Only query Overpass if we've expanded beyond what we already fetched
      if (currentRadius > lastQueriedRadius) {
        const bounds = this.radiusToBounds(center, currentRadius);
        const buildings = await this.getBuildingFootprints(bounds, (msg) => {
          // Don't show raw building count to user — show search progress instead
          if (!msg.startsWith('Found')) {
            onProgress?.(msg);
          }
        });
        lastQueriedRadius = currentRadius;

        // Merge new buildings into our cache (dedup by coordinates)
        for (const b of buildings) {
          const key = `${b.lat.toFixed(5)}-${b.lng.toFixed(5)}`;
          if (!seenCoords.has(key)) {
            seenCoords.add(key);
            allBuildings.push(b);
          }
        }
      }

      // Filter cached buildings to those within the current radius
      const buildingsInRadius = allBuildings.filter(b => {
        const dist = this.haversineDistance(center.lat, center.lng, b.lat, b.lng);
        return dist <= currentRadius;
      });

      // Find buildings we haven't geocoded yet
      const newBuildings = buildingsInRadius.filter(b => {
        const key = `geo-${b.lat.toFixed(5)}-${b.lng.toFixed(5)}`;
        return !seenAddresses.has(key);
      });

      if (newBuildings.length > 0 && window.google?.maps) {
        // Sort by distance from center (closest first)
        newBuildings.sort((a, b) => {
          const distA = this.haversineDistance(center.lat, center.lng, a.lat, a.lng);
          const distB = this.haversineDistance(center.lat, center.lng, b.lat, b.lng);
          return distA - distB;
        });

        // Only process what we need + a buffer for geocoding failures
        const remaining = targetCount - allAddresses.length;
        const buildingsToProcess = newBuildings.slice(0, remaining * 2);

        onProgress?.(`Geocoding ${buildingsToProcess.length} buildings (found ${allAddresses.length}/${targetCount})...`);

        const geocoder = new google.maps.Geocoder();
        const batchSize = 10;

        for (let i = 0; i < buildingsToProcess.length && allAddresses.length < targetCount; i += batchSize) {
          const batch = buildingsToProcess.slice(i, i + batchSize);

          const results = await Promise.all(
            batch.map(b => this.reverseGeocodePoint(geocoder, b))
          );

          for (const result of results) {
            if (result) {
              const addrKey = `${result.address.houseNumber}-${result.address.street}`.toLowerCase();
              if (!seenAddresses.has(addrKey)) {
                seenAddresses.add(addrKey);
                allAddresses.push(result.address);
              }
            }
          }

          // Mark all batch buildings as geocoded so we don't retry them
          for (const b of batch) {
            seenAddresses.add(`geo-${b.lat.toFixed(5)}-${b.lng.toFixed(5)}`);
          }

          onProgress?.(`Found ${allAddresses.length}/${targetCount} addresses...`);

          // Delay between batches
          if (i + batchSize < buildingsToProcess.length && allAddresses.length < targetCount) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
      }

      // If we still need more, expand radius
      if (allAddresses.length < targetCount) {
        currentRadius += radiusIncrement;
      }
    }

    // Sort by distance from center
    allAddresses.sort((a, b) => {
      const distA = this.haversineDistance(center.lat, center.lng, a.lat, a.lng);
      const distB = this.haversineDistance(center.lat, center.lng, b.lat, b.lng);
      return distA - distB;
    });

    // Take only the target count
    const finalAddresses = allAddresses.slice(0, targetCount);

    onProgress?.(`Found ${finalAddresses.length} addresses`);

    return {
      addresses: finalAddresses,
      source: 'Google Geocoding (point-based)',
      queryTime: Date.now() - startTime,
      bounds: this.radiusToBounds(center, currentRadius),
    };
  }

  /**
   * Discover all proximal communities around a center point
   * Clusters addresses into logical neighborhood/apartment groups
   */
  async discoverProximalCommunities(
    center: { lat: number; lng: number },
    targetPerWorkload: number = 500,
    onProgress?: (message: string) => void,
    maxTotalHomes?: number
  ): Promise<{
    addresses: (AddressData & { communityId: string; communityName: string })[];
    communities: Array<{
      id: string;
      name: string;
      addressCount: number;
      bounds: { north: number; south: number; east: number; west: number };
    }>;
  }> {
    onProgress?.('Discovering communities in the area...');

    // Search area based on maxTotalHomes (larger for more homes)
    const estimatedRadius = maxTotalHomes ? Math.min(5000, Math.max(1000, maxTotalHomes / 2)) : 2000;
    const searchRadius = estimatedRadius;
    const bounds = this.radiusToBounds(center, searchRadius);

    // Get all buildings in the area
    const buildings = await this.getBuildingFootprints(bounds, onProgress);
    onProgress?.(`Found ${buildings.length} buildings, analyzing communities...`);

    if (buildings.length === 0 || !window.google?.maps) {
      return { addresses: [], communities: [] };
    }

    // Cluster buildings by proximity and type
    const clusters = this.clusterBuildings(buildings, center);
    onProgress?.(`Identified ${clusters.length} distinct communities`);

    const allAddresses: (AddressData & { communityId: string; communityName: string })[] = [];
    const communities: Array<{
      id: string;
      name: string;
      addressCount: number;
      bounds: { north: number; south: number; east: number; west: number };
    }> = [];

    const geocoder = new google.maps.Geocoder();

    // Process each cluster
    for (let clusterIndex = 0; clusterIndex < clusters.length; clusterIndex++) {
      const cluster = clusters[clusterIndex];
      onProgress?.(`Processing community ${clusterIndex + 1}/${clusters.length} (${cluster.buildings.length} buildings)...`);

      const clusterAddresses: AddressData[] = [];
      const seen = new Set<string>();

      // Geocode buildings in this cluster (limit to reasonable number)
      const buildingsToProcess = cluster.buildings.slice(0, Math.min(cluster.buildings.length, targetPerWorkload));

      const batchSize = 10;
      for (let i = 0; i < buildingsToProcess.length; i += batchSize) {
        const batch = buildingsToProcess.slice(i, i + batchSize);

        const results = await Promise.all(
          batch.map(b => this.reverseGeocodePoint(geocoder, b))
        );

        for (const result of results) {
          if (result && !seen.has(result.key)) {
            seen.add(result.key);
            clusterAddresses.push(result.address);
          }
        }

        // Delay between batches
        if (i + batchSize < buildingsToProcess.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      if (clusterAddresses.length > 0) {
        // Generate community name from most common street
        const streetCounts = new Map<string, number>();
        for (const addr of clusterAddresses) {
          const count = streetCounts.get(addr.street) || 0;
          streetCounts.set(addr.street, count + 1);
        }
        const mainStreet = [...streetCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || 'Area';

        const communityId = `community_${clusterIndex + 1}`;
        const communityName = cluster.isApartment
          ? `${mainStreet} Apartments`
          : `${mainStreet} Area`;

        // Calculate cluster bounds
        let north = -Infinity, south = Infinity, east = -Infinity, west = Infinity;
        for (const addr of clusterAddresses) {
          north = Math.max(north, addr.lat);
          south = Math.min(south, addr.lat);
          east = Math.max(east, addr.lng);
          west = Math.min(west, addr.lng);
        }

        communities.push({
          id: communityId,
          name: communityName,
          addressCount: clusterAddresses.length,
          bounds: { north, south, east, west },
        });

        // Add addresses with community info
        for (const addr of clusterAddresses) {
          allAddresses.push({
            ...addr,
            communityId,
            communityName,
          });
        }

        // Check if we've hit the max limit
        if (maxTotalHomes && allAddresses.length >= maxTotalHomes) {
          onProgress?.(`Reached max limit of ${maxTotalHomes} homes`);
          break;
        }
      }
    }

    // Trim to exact max if we went over
    const finalAddresses = maxTotalHomes ? allAddresses.slice(0, maxTotalHomes) : allAddresses;

    // Recalculate community counts if we trimmed
    if (maxTotalHomes && allAddresses.length > maxTotalHomes) {
      const communityCounts = new Map<string, number>();
      for (const addr of finalAddresses) {
        const count = communityCounts.get(addr.communityId) || 0;
        communityCounts.set(addr.communityId, count + 1);
      }
      for (const community of communities) {
        community.addressCount = communityCounts.get(community.id) || 0;
      }
      // Remove communities with 0 addresses
      const finalCommunities = communities.filter(c => c.addressCount > 0);
      onProgress?.(`Discovered ${finalAddresses.length} addresses in ${finalCommunities.length} communities (limited to ${maxTotalHomes})`);
      return { addresses: finalAddresses, communities: finalCommunities };
    }

    onProgress?.(`Discovered ${finalAddresses.length} addresses in ${communities.length} communities`);

    return { addresses: finalAddresses, communities };
  }

  /**
   * Cluster buildings into logical groups (neighborhoods/apartment complexes)
   */
  private clusterBuildings(
    buildings: Array<{ lat: number; lng: number; type: string }>,
    center: { lat: number; lng: number }
  ): Array<{
    buildings: Array<{ lat: number; lng: number; type: string }>;
    isApartment: boolean;
    centroid: { lat: number; lng: number };
  }> {
    // Simple clustering: group by proximity (100m threshold for same cluster)
    const CLUSTER_THRESHOLD = 100; // meters
    const clusters: Array<{
      buildings: Array<{ lat: number; lng: number; type: string }>;
      isApartment: boolean;
      centroid: { lat: number; lng: number };
    }> = [];

    // Sort buildings by distance from center
    const sortedBuildings = [...buildings].sort((a, b) => {
      const distA = this.haversineDistance(center.lat, center.lng, a.lat, a.lng);
      const distB = this.haversineDistance(center.lat, center.lng, b.lat, b.lng);
      return distA - distB;
    });

    const assigned = new Set<number>();

    for (let i = 0; i < sortedBuildings.length; i++) {
      if (assigned.has(i)) continue;

      const building = sortedBuildings[i];
      const cluster: Array<{ lat: number; lng: number; type: string }> = [building];
      assigned.add(i);

      // Find nearby buildings
      for (let j = i + 1; j < sortedBuildings.length; j++) {
        if (assigned.has(j)) continue;

        const other = sortedBuildings[j];
        const dist = this.haversineDistance(building.lat, building.lng, other.lat, other.lng);

        if (dist <= CLUSTER_THRESHOLD) {
          cluster.push(other);
          assigned.add(j);
        }
      }

      // Determine if this is an apartment complex
      const apartmentTypes = ['apartments', 'residential', 'apartment'];
      const isApartment = cluster.some(b =>
        apartmentTypes.includes(b.type.toLowerCase())
      ) || cluster.length > 10; // Many buildings close together = likely apartments

      // Calculate centroid
      const centroid = {
        lat: cluster.reduce((sum, b) => sum + b.lat, 0) / cluster.length,
        lng: cluster.reduce((sum, b) => sum + b.lng, 0) / cluster.length,
      };

      clusters.push({ buildings: cluster, isApartment, centroid });
    }

    // Merge small clusters that are close together
    const mergedClusters: typeof clusters = [];
    const clusterAssigned = new Set<number>();

    for (let i = 0; i < clusters.length; i++) {
      if (clusterAssigned.has(i)) continue;

      const mainCluster = { ...clusters[i], buildings: [...clusters[i].buildings] };
      clusterAssigned.add(i);

      // Look for nearby small clusters to merge
      for (let j = i + 1; j < clusters.length; j++) {
        if (clusterAssigned.has(j)) continue;

        const other = clusters[j];
        const dist = this.haversineDistance(
          mainCluster.centroid.lat,
          mainCluster.centroid.lng,
          other.centroid.lat,
          other.centroid.lng
        );

        // Merge if close and combined size is reasonable
        if (dist <= 200 && mainCluster.buildings.length + other.buildings.length <= 600) {
          mainCluster.buildings.push(...other.buildings);
          mainCluster.isApartment = mainCluster.isApartment || other.isApartment;
          clusterAssigned.add(j);

          // Recalculate centroid
          mainCluster.centroid = {
            lat: mainCluster.buildings.reduce((sum, b) => sum + b.lat, 0) / mainCluster.buildings.length,
            lng: mainCluster.buildings.reduce((sum, b) => sum + b.lng, 0) / mainCluster.buildings.length,
          };
        }
      }

      mergedClusters.push(mainCluster);
    }

    // Sort clusters by distance from center
    mergedClusters.sort((a, b) => {
      const distA = this.haversineDistance(center.lat, center.lng, a.centroid.lat, a.centroid.lng);
      const distB = this.haversineDistance(center.lat, center.lng, b.centroid.lat, b.centroid.lng);
      return distA - distB;
    });

    return mergedClusters;
  }
}

export const addressDatabaseService = new AddressDatabaseService();
export default addressDatabaseService;
